import {
  type IntegrationOperation,
  type ProviderImportProfile,
  type ResourceLabelsByProfile,
  type ResourceType,
  ResourceTypeEnums,
  type SyncSettingConfig,
} from '@timelyapp/integration-cloud-api-spec';
import type { GetSubscribableResourcesApiResponse } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import type { SyncSettingsProps } from './util.syncSettings';

/**
 * Supported template placeholders:
 *   {provider}, {resourceType}, {resourceTypePlural}
 */

type SyncSettingKey = SyncSettingConfig['key'];

const SYNC_KEY_TO_RESOURCE_TYPE: Partial<Record<SyncSettingKey, ResourceType>> = {
  sync_projects: 'project',
  sync_clients: 'client',
  sync_tasks: 'task',
  reference_tasks: 'task',
  sync_tags: 'tag',
  sync_teams: 'team',
  project_add_all_workspace_users: 'project',
  project_replace_users_on_update: 'project',
  project_send_invites: 'project',
  project_require_labels: 'project',
  project_auto_add_assignees_to_projects: 'project',
};

const RESOURCE_ROLE_DISPLAY: Partial<Record<ResourceType, string>> = {
  client: 'Client',
  project: 'Project',
  task: 'Task',
  tag: 'Tag',
  organization: 'Organization',
  team: 'Team',
  pull_request: 'Pull Request',
};

const RESOURCE_TYPE_SET = new Set<ResourceType>(ResourceTypeEnums);

const PLACEHOLDER_PROVIDER = '{provider}';
const PLACEHOLDER_RESOURCE_TYPE = '{resourceType}';
const PLACEHOLDER_RESOURCE_TYPE_PLURAL = '{resourceTypePlural}';
const KNOWN_TEMPLATE_PLACEHOLDERS = new Set(
  [PLACEHOLDER_PROVIDER, PLACEHOLDER_RESOURCE_TYPE, PLACEHOLDER_RESOURCE_TYPE_PLURAL].map(
    placeholder => placeholder.toLowerCase()
  )
);

export function getActiveImportProfile<TKey extends string = string>(
  profiles: ProviderImportProfile[] | undefined,
  desiredKey?: TKey | null
): ProviderImportProfile | undefined {
  if (!profiles || profiles.length === 0) return undefined;

  if (desiredKey) {
    const match = profiles.find(profile => profile.key === desiredKey);
    if (match) return match;
  }

  const defaultProfile = profiles.find(profile => profile.isDefault);
  return defaultProfile ?? profiles[0];
}

export function getProfileAwareSource(
  setting: Pick<SyncSettingConfig | SyncSettingsProps, 'source' | 'key'>,
  profile: ProviderImportProfile | undefined,
  providerTitle: string
): string | undefined {
  if (!setting.source) return undefined;

  warnOnUnknownPlaceholders(setting.source);
  const needsResourcePlaceholder = templateIncludesResourcePlaceholder(setting.source);

  const replacements: Record<string, string> = {
    [PLACEHOLDER_PROVIDER]: providerTitle,
  };

  if (profile?.resourceLabels) {
    const resourceType = SYNC_KEY_TO_RESOURCE_TYPE[setting.key];
    const resourceLabels = resourceType ? profile.resourceLabels[resourceType] : undefined;

    if (!resourceType && needsResourcePlaceholder) {
      warnMissingResourceTypeMapping(setting.key);
    } else if (resourceLabels) {
      replacements[PLACEHOLDER_RESOURCE_TYPE] = resourceLabels.singular;
      replacements[PLACEHOLDER_RESOURCE_TYPE_PLURAL] = resourceLabels.plural;
    }
  }

  return replaceTemplate(setting.source, replacements);
}

type ProfileLabelsProps = Pick<
  GetSubscribableResourcesApiResponse['subscriptions'][number],
  'external_type'
> & {
  resource_type?: ResourceType | string | null;
};

export function getResourceContextLabel(
  resource: ProfileLabelsProps,
  profile: ProviderImportProfile | undefined
): string | undefined {
  const resourceLabels: ResourceLabelsByProfile | undefined = profile?.resourceLabels;
  let resourceType = normalizeResourceType(resource.resource_type);

  if (!resourceType && resourceLabels && resource.external_type) {
    const externalType = resource.external_type.toLowerCase();
    for (const [type, labels] of Object.entries(resourceLabels)) {
      if (!labels) continue;
      const singularMatch = labels.singular?.toLowerCase() === externalType;
      const pluralMatch = labels.plural?.toLowerCase() === externalType;
      if (singularMatch || pluralMatch) {
        if (RESOURCE_TYPE_SET.has(type as ResourceType)) {
          resourceType = type as ResourceType;
          break;
        }
      }
    }
  }

  if (!resourceType) {
    return resource.external_type ? capitalize(resource.external_type) : undefined;
  }

  const label = resourceLabels?.[resourceType];
  const role = RESOURCE_ROLE_DISPLAY[resourceType] ?? capitalize(resourceType);

  const base = label?.singular ?? resource.external_type ?? role.toLowerCase();

  if (base.toLowerCase() === role.toLowerCase()) {
    return role;
  }
  return `${capitalize(base)} (${role})`;
}

export function getProfileAwareOperationTitle(
  operation: Pick<IntegrationOperation, 'operationId' | 'title' | 'titleByProfile'>,
  profile: ProviderImportProfile | undefined
): string {
  const profileKey = profile?.key;
  const overrideTitle = profileKey ? operation.titleByProfile?.[profileKey] : undefined;
  return overrideTitle ?? operation.title ?? operation.operationId ?? '';
}

/**
 * Resolves the effective import profile key based on scope and available fallbacks.
 *
 * Redux state starts null; once connection loads, connectionProfileKey takes effect.
 * During setup wizard, user selection (selectedProfileKey) temporarily overrides.
 * Personal scope always uses connection/default (user selections ignored post-setup).
 */
export function resolveImportProfileKey(params: {
  scope: 'personal' | 'account';
  selectedProfileKey?: string | null;
  connectionProfileKey?: string | null;
  defaultProfileKey?: string | null;
}): string | null {
  const { scope, selectedProfileKey, connectionProfileKey, defaultProfileKey } = params;

  if (scope === 'account') {
    // For new connections in setup: selection (null) → connection (null) → default
    // For existing connections: selection (null) → connection (loaded) → default
    // During active selection: selection (set) → connection → default
    return selectedProfileKey ?? connectionProfileKey ?? defaultProfileKey ?? null;
  }

  return connectionProfileKey ?? defaultProfileKey ?? null;
}

export function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function replaceTemplate(template: string, replacements: Record<string, string>): string {
  return Object.entries(replacements).reduce((current, [placeholder, value]) => {
    if (!value) return current;
    const pattern = new RegExp(escapeRegExp(placeholder), 'gi');
    return current.replace(pattern, value);
  }, template);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function warnOnUnknownPlaceholders(template: string): void {
  if (process.env.NODE_ENV === 'production') return;
  const openCount = (template.match(/\{/g) || []).length;
  const closeCount = (template.match(/\}/g) || []).length;
  if (openCount !== closeCount) {
    console.warn(`Template has mismatched braces: ${template}`);
  }
  const matches = template.match(/\{[^}]+\}/g) ?? [];
  const unknown = matches.filter(
    placeholder => !KNOWN_TEMPLATE_PLACEHOLDERS.has(placeholder.toLowerCase())
  );
  if (unknown.length > 0) {
    console.warn(`Unknown placeholder(s) in template: ${unknown.join(', ')}`);
  }
}

function warnMissingResourceTypeMapping(syncKey: SyncSettingKey): void {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(
    `No resource type mapping found for sync key "${syncKey}". Update SYNC_KEY_TO_RESOURCE_TYPE if needed.`
  );
}

function templateIncludesResourcePlaceholder(template: string): boolean {
  const lower = template.toLowerCase();
  return (
    lower.includes(PLACEHOLDER_RESOURCE_TYPE.toLowerCase()) ||
    lower.includes(PLACEHOLDER_RESOURCE_TYPE_PLURAL.toLowerCase())
  );
}

function normalizeResourceType(
  value: ProfileLabelsProps['resource_type']
): ResourceType | undefined {
  if (typeof value !== 'string') return undefined;
  return RESOURCE_TYPE_SET.has(value as ResourceType) ? (value as ResourceType) : undefined;
}
